import { Clear } from '@dreamstack/icons/src/icon-components/Clear'
import { useGetCookieConsentAccepted } from '@dreamstack/util'
import type { FullProperty } from '@dreamstack/web/lib/prisma/properties'
import { Dialog, DialogBackdrop } from '@headlessui/react'
import { AnimatePresence, motion } from 'framer-motion'
import first from 'lodash/first'
import last from 'lodash/last'
import map from 'lodash/map'
import split from 'lodash/split'
import take from 'lodash/take'
import Image from 'next/image'
import type { CSSProperties, FunctionComponent } from 'react'
import { useState } from 'react'
import { transformPropstackImageUrl } from './propstack-image-url-transform'
import { useTranslation } from '../../../i18n/src/lang/useTranslation'
import { useIsSmOrGreater } from '../hooks/useWindowSize'
import { useNumberOfPreviews } from '../hooks/useNumberOfPreviews'
import { AccentroSlider } from '../keenSlider/AccentroSlider'
import { AspectRatioContainer } from '../ui/AspectRatioContainer'
import { EmbeddedYoutubeVideo } from '../ui/EmbeddedYoutubeVideo'
import { YoutubePlayButton } from '../ui/YoutubePlayButton'
import type { Slide } from '../keenSlider/AccentroSlider'
import styles from './PropertyImageSlider.module.css'

// TODO: Place somewhere else.
const fallbackImageUrl =
  'https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1459&q=80'

const overlayBaseClass =
  'absolute inset-0 flex justify-center items-center bg-accentroGray-600 text-accentroWhite-full'

const overlayContentClass = 'flex flex-col justify-center items-center p-px-16'

const overlayCountClass =
  'font-semibold text-px-24 leading-px-30 sm:text-px-28 sm:leading-px-42 ml:text-px-40 ml:leading-px-50'

const overlayLabelClass =
  'font-semibold text-center text-px-13 leading-px-18 ml:text-px-20 ml:leading-px-30'

const slideWrapperClass =
  'max-h-[80vh] max-w-[80vw] flex flex-col justify-center items-center m-auto'

const dialogWrapperClass =
  'fixed z-50 inset-0 flex items-center justify-center max-w-[98vw]'

const dialogOverlayClass = 'fixed inset-0 backdrop-blur-sm bg-accentroGray-800'

const dialogCloseButtonClass =
  'absolute text-accentroWhite-full top-px-16 right-px-16 md:top-px-32 md:right-px-32 cursor-pointer'

const closeIconClass = 'h-px-32 w-px-32'

const thumbnailsWrapperClass =
  'my-px-80 flex flex-wrap gap-px-16 col-span-full justify-center ml:gap-0 ml:space-x-px-32'

const thumbContainerClass =
  'w-full cursor-pointer flex-1 relative text-sm md:text-lg'

const videoThumbOverlayClass =
  'absolute inset-0 flex justify-center items-center'

const getThumbStyles = (noOfPreviews: number): CSSProperties => {
  const size = `calc((100% - ${(noOfPreviews - 1) * 16}px) / ${noOfPreviews})`
  // We expose the computed size as a CSS variable so the CSS module can decide
  // whether to apply min-width based on a media query (>= 660px)
  return {
    paddingTop: size,
    ['--thumb-size' as any]: size,
  }
}

export const PropertyImageSlider: FunctionComponent<
  React.PropsWithChildren<{
    hasPrice: boolean
    property?: FullProperty
    images: { title?: string | null; url: string; id: number }[]
  }>
> = ({ property, images = [] }) => {
  const t = useTranslation()

  const [isOpen, setIsOpen] = useState(false)

  const numberOfPreviews = useNumberOfPreviews()
  const isSmOrGreater = useIsSmOrGreater()

  const [currentSlide, setCurrentSlide] = useState<number>(0)

  const youtubeCookiesAccepted = useGetCookieConsentAccepted({
    consentId: 'BJz7qNsdj-7|iVTAGV51C',
  })

  const videoLinks: string[] = property?.film_links ?? []
  const videoSlides = map(videoLinks, (videoLink, index) => {
    return {
      title: 'Video ' + (index + 1),
      type: 'video',
      slide: (
        <div className={slideWrapperClass} key={index}>
          <AspectRatioContainer width={16} height={9} percentOfViewPort={80}>
            <EmbeddedYoutubeVideo fullYoutubeUrl={videoLink} />
          </AspectRatioContainer>
        </div>
      ),
    } as Slide
  })
  const imageSlides = images.map((img, index) => {
    const imageUrl = transformPropstackImageUrl({
      url: img.url,
      size: 'default',
    })
    const slide: Slide = {
      title: img.title,
      type: 'picture',
      slide: (
        <div className={slideWrapperClass} key={index}>
          <AspectRatioContainer width={3} height={2} percentOfViewPort={80}>
            <Image
              quality={75}
              alt={img.title ?? 'property image'}
              src={imageUrl ?? fallbackImageUrl}
              layout="fill"
              sizes={'312, 624, 1110'}
              priority
              className="object-contain"
            />
          </AspectRatioContainer>
        </div>
      ),
    }
    return slide
  })

  const imageThumbnails = images.map((img, index) => {
    const imageUrl = transformPropstackImageUrl({ url: img.url, size: 'thumb' })
    const slide: Slide = {
      title: img.title,
      slide: (
        <Image
          key={index}
          alt={img.title ? img.title + 'thumbnail' : 'property image thumbnail'}
          src={imageUrl ?? fallbackImageUrl}
          layout="fill"
          className="object-contain"
        />
      ),
    }
    return slide
  })

  const videoThumbnails = videoLinks.map((videoLink, index) => {
    const lastUrlPart = last(split(videoLink, '/'))
    let embedId = last(split(lastUrlPart, '='))
    embedId = first(split(embedId, '#'))

    const thumbUrl = youtubeCookiesAccepted
      ? `https://img.youtube.com/vi/${embedId}/hqdefault.jpg`
      : ''

    return {
      slide: (
        <div
          key={index}
          className={videoThumbOverlayClass}
          style={{
            backgroundPosition: 'center',
            backgroundImage: `linear-gradient(rgba(43,45,54,0.6), rgba(43,45,54,0.6)), url(${thumbUrl})`,
          }}
        >
          <YoutubePlayButton />
        </div>
      ),
    } as Slide
  }) as Slide[]

  const thumbnails = videoThumbnails.concat(imageThumbnails)
  const topThumbs = take(thumbnails, numberOfPreviews)
  const countOfFurtherImages = thumbnails.length - topThumbs.length

  return (
    <>
      <div className="col-span-full">
        <AnimatePresence>
          {isOpen && (
            <Dialog
              key={'motion-dialog'}
              as="div"
              className={dialogWrapperClass}
              aria-labelledby="form-dialog-title"
              onClose={() => {
                setIsOpen(false)
              }}
              open={isOpen}
              static
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <DialogBackdrop className={dialogOverlayClass} />
                <button
                  className={dialogCloseButtonClass}
                  onClick={() => setIsOpen(false)}
                >
                  <Clear className={closeIconClass} />
                </button>
                <AccentroSlider
                  currentSlide={currentSlide}
                  thumbnails={thumbnails}
                  optionType={'PropImageSlider'}
                  videos={videoSlides}
                  slides={imageSlides}
                  useBottomNavigation
                />
              </motion.div>
            </Dialog>
          )}
        </AnimatePresence>
      </div>
      <div className={thumbnailsWrapperClass}>
        {map(topThumbs, (thumb, i) => {
          const isLast = i === topThumbs.length - 1
          const previews = isSmOrGreater
            ? numberOfPreviews
            : numberOfPreviews / 2
          return (
            <div
              className={`${thumbContainerClass} ${styles.thumbContainer}`}
              style={getThumbStyles(previews)}
              onClick={() => {
                setCurrentSlide(i)
                setIsOpen(true)
              }}
              onKeyPress={() => setIsOpen(true)}
              role="button"
              tabIndex={i}
              key={i}
            >
              {thumb.slide}
              {isLast && countOfFurtherImages > 0 && (
                <div className={overlayBaseClass}>
                  <div className={overlayContentClass}>
                    <p className={overlayCountClass}>{countOfFurtherImages}</p>
                    <p className={overlayLabelClass}>
                      {t('accentro:imageSlider.furtherPhotosAndVideos')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}
